import type { FC } from "react";

import classNames from "classnames";

import { Checkbox } from "@jti/core-components/components/Checkbox";
import type { SecuredFieldsFieldErrorType } from "@jti/fe-common";
import { Tooltip, useTranslations } from "@jti/ui";

import { SecuredFieldsErrorMessage, SecuredFieldsFormSkeleton } from "./components";
import { FIELD_IDS } from "./SecuredFieldsForm.constants";

import styles from "./SecuredFieldsForm.module.scss";

export { FIELD_IDS, FIELD_SELECTORS } from "./SecuredFieldsForm.constants";

type FieldErrors = Partial<Record<string, SecuredFieldsFieldErrorType>>;

type SecuredFieldsFormProps = {
  errors?: FieldErrors | null;
  hasGeneralValidationError?: boolean;
  isLoading?: boolean;
  isReady?: boolean;
  isCardSaved: boolean;
  onSaveCardChange: (isCardSaved: boolean) => void;
};

export const SecuredFieldsForm: FC<SecuredFieldsFormProps> = ({
  errors,
  hasGeneralValidationError,
  isLoading,
  isReady,
  isCardSaved,
  onSaveCardChange,
}) => {
  const { __ } = useTranslations();

  const isFieldInvalid = (key: string) => !!errors?.[key] || !!hasGeneralValidationError;

  const getInputWrapperClassName = (key: string) =>
    classNames(styles.inputWrapper, {
      [styles.inputError]: isFieldInvalid(key),
    });

  if (!isLoading && !isReady) {
    return null;
  }

  return (
    <div className={styles.container} data-testid="secured-fields-form">
      {isLoading && <SecuredFieldsFormSkeleton />}

      {isReady && (
        <>
          <div className={styles.field}>
            <label htmlFor={FIELD_IDS.cardNumber}>{__("cart.checkout.secured_fields.card_number")}</label>
            <div className={getInputWrapperClassName("cardNumber")}>
              <input
                placeholder={__("cart.checkout.secured_fields.placeholder.card_number")}
                type="text"
                id={FIELD_IDS.cardNumber}
                className={styles.input}
                readOnly
                aria-label={__("cart.checkout.secured_fields.card_number")}
                aria-invalid={isFieldInvalid("cardNumber")}
                aria-describedby={errors?.cardNumber ? "secured-fields-card-number-error" : undefined}
              />
            </div>
            <SecuredFieldsErrorMessage
              errorId="secured-fields-card-number-error"
              errorValue={errors?.cardNumber}
              translationKey="card_number"
            />
          </div>

          <div className={styles.expiryRow}>
            <div className={classNames(styles.field, styles.expiryRowItem)}>
              <label htmlFor={FIELD_IDS.expiryMonth}>{__("cart.checkout.secured_fields.expiry_month")}</label>
              <div className={getInputWrapperClassName("expiryMonth")}>
                <input
                  placeholder={__("cart.checkout.secured_fields.placeholder.expiry_month")}
                  type="text"
                  id={FIELD_IDS.expiryMonth}
                  className={styles.input}
                  readOnly
                  aria-label={__("cart.checkout.secured_fields.expiry_month")}
                  aria-invalid={isFieldInvalid("expiryMonth")}
                  aria-describedby={errors?.expiryMonth ? "secured-fields-expiry-month-error" : undefined}
                />
              </div>
              <SecuredFieldsErrorMessage
                errorId="secured-fields-expiry-month-error"
                errorValue={errors?.expiryMonth}
                translationKey="expiry_month"
              />
            </div>

            <div className={classNames(styles.field, styles.expiryRowItem)}>
              <label htmlFor={FIELD_IDS.expiryYear}>{__("cart.checkout.secured_fields.expiry_year")}</label>
              <div className={getInputWrapperClassName("expiryYear")}>
                <input
                  placeholder={__("cart.checkout.secured_fields.placeholder.expiry_year")}
                  type="text"
                  id={FIELD_IDS.expiryYear}
                  className={styles.input}
                  readOnly
                  aria-label={__("cart.checkout.secured_fields.expiry_year")}
                  aria-invalid={isFieldInvalid("expiryYear")}
                  aria-describedby={errors?.expiryYear ? "secured-fields-expiry-year-error" : undefined}
                />
              </div>
              <SecuredFieldsErrorMessage
                errorId="secured-fields-expiry-year-error"
                errorValue={errors?.expiryYear}
                translationKey="expiry_year"
              />
            </div>
          </div>

          <div className={styles.field}>
            <label htmlFor={FIELD_IDS.securityCode}>{__("cart.checkout.secured_fields.security_code")}</label>
            <div className={styles.securityCodeRow}>
              <div className={getInputWrapperClassName("securityCode")}>
                <input
                  placeholder={__("cart.checkout.secured_fields.placeholder.security_code")}
                  type="text"
                  id={FIELD_IDS.securityCode}
                  className={styles.input}
                  readOnly
                  aria-label={__("cart.checkout.secured_fields.security_code")}
                  aria-invalid={isFieldInvalid("securityCode")}
                  aria-describedby={errors?.securityCode ? "secured-fields-security-code-error" : undefined}
                />
              </div>
              <Tooltip
                content={__("cart.checkout.secured_fields.tooltip.content")}
                iconClassName={styles.tooltipIcon}
              />
            </div>
            <SecuredFieldsErrorMessage
              errorId="secured-fields-security-code-error"
              errorValue={errors?.securityCode}
              translationKey="security_code"
            />
          </div>

          <div className={styles.field}>
            <label htmlFor={FIELD_IDS.nameOnCard}>{__("cart.checkout.secured_fields.cardholder_name")}</label>
            <div className={getInputWrapperClassName("nameOnCard")}>
              <input
                placeholder={__("cart.checkout.secured_fields.placeholder.cardholder_name")}
                type="text"
                id={FIELD_IDS.nameOnCard}
                className={styles.input}
                readOnly
                aria-label={__("cart.checkout.secured_fields.cardholder_name")}
                aria-invalid={isFieldInvalid("nameOnCard")}
                aria-describedby={errors?.nameOnCard ? "secured-fields-cardholder-name-error" : undefined}
              />
            </div>
            <SecuredFieldsErrorMessage
              errorId="secured-fields-cardholder-name-error"
              errorValue={errors?.nameOnCard}
              translationKey="cardholder_name"
            />
          </div>

          <div className={styles.checkbox}>
            <Checkbox
              checked={isCardSaved}
              onCheckedChange={onSaveCardChange}
              label={__("cart.checkout.secured_fields.save_card")}
            />
          </div>
        </>
      )}
    </div>
  );
};
